import {
    ApiTypeGuardError,
    createEnum,
    decodeModelWith,
    extendGuard,
    nullable,
} from '@24i/nxg-core-utils/src/guards';
import * as t from 'io-ts';
import { getRuntimeConfig } from '@24i/nxg-sdk-smartott/src/Application/initApp';
import { ProductGuard, Product } from './product';

export enum SubscriptionStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    CANCELLED = 'cancelled',
}

export const SubscriptionGuard = extendGuard(
    'Subscription',
    ProductGuard,
    {
        type: t.literal('subscription'),
    },
    {
        period: nullable(t.string),
        status: nullable(createEnum<SubscriptionStatus>(SubscriptionStatus, 'SubscriptionStatus')),
        expirationDate: nullable(t.number),
        isWebPayment: nullable(t.boolean),
        paymentMethod: nullable(t.string),
    }
);

export type Subscription = t.TypeOf<typeof SubscriptionGuard>;

export const isSubscription = (data: unknown): data is Subscription => {
    try {
        return !!decodeModelWith(SubscriptionGuard, data, 'Subscription', {
            disableErrorLog: true,
        });
    } catch {
        return false;
    }
};

export const createSubscription = (data: Product): Subscription | never => {
    const subscriptionObj = {
        isWebPayment: ['stripe', 'amex', 'visa', 'card'].includes(data?.paymentMethod || ''),
        ...data,
    };

    try {
        return decodeModelWith(SubscriptionGuard, subscriptionObj, 'Subscription');
    } catch (error) {
        if (error instanceof Error && error.name === 'ModelTypeGuardError') {
            const typedError = error as ApiTypeGuardError<Subscription>;
            const serviceGuardEnabled = getRuntimeConfig('backstageServiceGuards')?.enabled;
            if (serviceGuardEnabled) {
                if (__DEV__) {
                    // eslint-disable-next-line no-console
                    console.error(
                        'API Model errors: ',
                        typedError.message,
                        typedError.rejectedPayload
                    );
                } else {
                    // TODO: hook this properly to analytics when analytics provider is implemented
                    // indeed preferably it should be the selected monitoring client
                    // Sentry?.captureException(error);
                }
            }
            return subscriptionObj as Subscription;
        }
        throw error;
    }
};
